let predictiveSearchInstanceCounter = 0;

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.instanceId = (this.input.id || `PredictiveSearch-${++predictiveSearchInstanceCounter}`).replace(
      /[^a-zA-Z0-9_-]/g,
      '-'
    );
    this.resultsId = `${this.instanceId}-Results`;
    this.predictiveSearchResults.id = this.resultsId;
    this.input.setAttribute('aria-controls', this.resultsId);
    this.input.setAttribute('aria-owns', this.resultsId);
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.blockedElements = new Map();
    this.escapeHandled = false;
    this.onViewportChange = this.onViewportChange.bind(this);

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    window.addEventListener('resize', this.onViewportChange);
    window.visualViewport?.addEventListener('resize', this.onViewportChange);
    window.visualViewport?.addEventListener('scroll', this.onViewportChange);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onViewportChange);
    window.visualViewport?.removeEventListener('resize', this.onViewportChange);
    window.visualViewport?.removeEventListener('scroll', this.onViewportChange);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('[data-predictive-search-groups]')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 150);
  }

  onKeyup(event) {
    if (event.code === 'Escape' && this.escapeHandled) {
      this.escapeHandled = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'Escape' && this.hasAttribute('open')) {
      this.escapeHandled = true;
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }

    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      if (currentButtonText.split(previousTerm).length - 1 > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const relevance = window.ZicoSearchRelevance;
    const requestTerm = relevance?.buildPredictiveQuery?.(searchTerm) || searchTerm;
    const queryKey = `${searchTerm}::${requestTerm}`.replace(/\s+/g, '-').toLowerCase();
    this.abortController.abort();
    this.abortController = new AbortController();
    const requestController = this.abortController;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    const searchParams = new URLSearchParams({
      q: requestTerm,
      'resources[type]': 'product',
      'resources[limit]': '8',
      'resources[options][fields]': 'title,vendor',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search',
    });

    fetch(`${routes.predictive_search_url}?${searchParams.toString()}`, {
      signal: requestController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (requestController.signal.aborted || searchTerm !== this.getQuery()) {
          throw new DOMException('Aborted', 'AbortError');
        }

        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        if (requestTerm !== searchTerm && resultsMarkup) {
          resultsMarkup
            .querySelectorAll('[data-predictive-search-search-for-text]')
            .forEach((element) => {
              element.textContent = element.textContent.replace(requestTerm, searchTerm);
            });
          resultsMarkup.querySelectorAll('a[href]').forEach((anchor) => {
            const url = new URL(anchor.getAttribute('href'), window.location.origin);
            if (url.pathname === routes.search_url || url.pathname === '/search') {
              anchor.href = relevance?.buildSearchUrl?.(searchTerm)?.toString() || `/search?q=${encodeURIComponent(searchTerm)}`;
            }
          });
        }
        if (!resultsMarkup) throw new Error('Predictive search section markup is missing');
        const resultsHtml = resultsMarkup.innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsHtml;
        });
        this.renderSearchResults(resultsHtml);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  namespaceResultsMarkup(resultsMarkup) {
    const template = document.createElement('template');
    template.innerHTML = resultsMarkup;
    const idMap = new Map();

    template.content.querySelectorAll('[id]').forEach((element) => {
      const originalId = element.id;
      const namespacedId = `${this.instanceId}-${originalId}`;
      idMap.set(originalId, namespacedId);
      element.id = namespacedId;
    });

    ['aria-activedescendant', 'aria-controls', 'aria-describedby', 'aria-labelledby', 'aria-owns', 'for'].forEach(
      (attribute) => {
        template.content.querySelectorAll(`[${attribute}]`).forEach((element) => {
          const value = element.getAttribute(attribute);
          if (!value) return;
          element.setAttribute(
            attribute,
            value
              .split(/\s+/)
              .map((token) => idMap.get(token) || token)
              .join(' ')
          );
        });
      }
    );

    template.content.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      const targetId = anchor.getAttribute('href').slice(1);
      if (idMap.has(targetId)) anchor.setAttribute('href', `#${idMap.get(targetId)}`);
    });

    return template.innerHTML;
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = this.namespaceResultsMarkup(resultsMarkup);
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countValue = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countValue) this.setLiveRegionText(countValue.textContent);
  }

  setUnderlyingContentBlocked(shouldBlock) {
    if (shouldBlock) {
      const candidates = document.querySelectorAll(
        '#ProductGridContainer, #product-grid, .product-grid-container, .facets-container, [data-zico-load-more]'
      );
      candidates.forEach((element) => {
        if (this.contains(element) || element.contains(this) || this.blockedElements.has(element)) return;
        this.blockedElements.set(element, {
          hadInert: element.hasAttribute('inert'),
          ariaHidden: element.getAttribute('aria-hidden'),
        });
        element.setAttribute('inert', '');
        element.setAttribute('aria-hidden', 'true');
        element.dataset.zicoPredictiveBlocked = 'true';
      });
      document.body.classList.add('zico-predictive-search-open');
      return;
    }

    this.blockedElements.forEach((state, element) => {
      if (!element.isConnected) return;
      if (!state.hadInert) element.removeAttribute('inert');
      if (state.ariaHidden === null) element.removeAttribute('aria-hidden');
      else element.setAttribute('aria-hidden', state.ariaHidden);
      delete element.dataset.zicoPredictiveBlocked;
    });
    this.blockedElements.clear();

    const anotherSearchIsOpen = Array.from(document.querySelectorAll('predictive-search')).some(
      (instance) => instance !== this && instance.hasAttribute('open')
    );
    if (!anotherSearchIsOpen) document.body.classList.remove('zico-predictive-search-open');
  }

  getResultsMaxHeight() {
    const resultsTop = this.predictiveSearchResults.getBoundingClientRect().top;
    const viewportBottom = window.visualViewport
      ? window.visualViewport.offsetTop + window.visualViewport.height
      : window.innerHeight;
    this.resultsMaxHeight = Math.max(0, Math.floor(viewportBottom - resultsTop - 12));
    return this.resultsMaxHeight;
  }

  onViewportChange() {
    if (!this.isOpen) return;
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
  }

  open() {
    document.querySelectorAll('predictive-search[open]').forEach((instance) => {
      if (instance !== this && typeof instance.close === 'function') instance.close();
    });
    this.setAttribute('open', true);
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.input.setAttribute('aria-expanded', true);
    this.setUnderlyingContentBlocked(true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    this.setUnderlyingContentBlocked(false);
    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
